import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import { useSocket } from '../../context/SocketContext';
import { useToast } from '../../context/ToastContext';
import WallProfileModal from '../Wall/WallProfileModal';

const styles = {
  container: { padding: '0' },
  newMatches: {
    padding: '16px 20px',
    borderBottom: '1px solid var(--border-color)',
  },
  newMatchesTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '12px',
  },
  newMatchesRow: {
    display: 'flex',
    gap: '12px',
    overflowX: 'auto',
    paddingBottom: '8px',
  },
  newMatchCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
    cursor: 'pointer',
    flexShrink: 0,
  },
  newMatchAvatar: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    objectFit: 'cover',
    background: 'var(--bg-elevated)',
    border: '2px solid var(--accent)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    color: 'var(--text-muted)',
  },
  newMatchName: {
    fontSize: '12px',
    fontWeight: '500',
    maxWidth: '70px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'center',
  },
  chatItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '14px 20px',
    cursor: 'pointer',
    borderBottom: '1px solid var(--border-color)',
    transition: 'background 200ms',
  },
  avatarWrap: {
    position: 'relative',
    flexShrink: 0,
  },
  avatar: {
    width: '52px',
    height: '52px',
    borderRadius: '50%',
    objectFit: 'cover',
    background: 'var(--bg-elevated)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    color: 'var(--text-muted)',
  },
  chatInfo: {
    flex: 1,
    minWidth: 0,
  },
  chatName: {
    fontSize: '15px',
    fontWeight: '600',
    marginBottom: '3px',
  },
  chatPreview: {
    fontSize: '13px',
    color: 'var(--text-secondary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  chatMeta: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '4px',
    flexShrink: 0,
  },
  chatTime: {
    fontSize: '11px',
    color: 'var(--text-muted)',
  },
  unreadBadge: {
    background: 'var(--accent)',
    color: '#fff',
    fontSize: '11px',
    fontWeight: '700',
    minWidth: '20px',
    height: '20px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 6px',
  },
};

function formatTime(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'short' });
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export default function MatchList() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileUserId, setProfileUserId] = useState(null);
  const { onlineUsers, incomingMessage } = useSocket();
  const { showToast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get('/matches')
      .then((res) => setMatches(res.data))
      .catch(() => { showToast('Couldn\'t load matches', 'error'); })
      .finally(() => setLoading(false));
  }, []);

  // Refresh when a new message comes in
  useEffect(() => {
    if (incomingMessage?.match_id) {
      api.get('/matches').then((res) => setMatches(res.data)).catch(() => {});
    }
  }, [incomingMessage]);

  if (loading) {
    return (
      <div style={{ padding: '0 14px' }}>
        {[...Array(5)].map((_, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 0', borderBottom: '1px solid var(--border-color)' }}>
            <div className="skeleton" style={{ width: 52, height: 52, borderRadius: '50%', flexShrink: 0 }} />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div className="skeleton" style={{ width: 120, height: 14 }} />
              <div className="skeleton" style={{ width: 180, height: 12 }} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (matches.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">♥</div>
        <h3 className="empty-state-title">No matches yet</h3>
        <p>Start swiping to find your matches!</p>
        <button onClick={() => navigate('/discover')} className="btn btn-primary" style={{ marginTop: 16 }}>
          Start Swiping →
        </button>
      </div>
    );
  }

  const newMatches = matches.filter((m) => !m.last_message);
  const conversations = matches.filter((m) => m.last_message);

  return (
    <div style={styles.container}>
      {newMatches.length > 0 && (
        <div style={styles.newMatches}>
          <h3 style={styles.newMatchesTitle}>New matches</h3>
          <div style={styles.newMatchesRow}>
            {newMatches.map((m) => (
              <div
                key={m.id}
                style={styles.newMatchCard}
                onClick={() => setProfileUserId(m.other_user.id)}
              >
                <div style={styles.newMatchAvatar}>
                  {m.other_user.photo ? (
                    <img
                      src={`/uploads/${m.other_user.photo}`}
                      alt=""
                      style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                    />
                  ) : (
                    m.other_user.display_name?.[0]?.toUpperCase() || '?'
                  )}
                </div>
                <span style={styles.newMatchName}>{m.other_user.display_name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {conversations.map((m) => {
        const isOnline = onlineUsers.has(m.other_user.id);
        return (
          <div
            key={m.id}
            style={styles.chatItem}
            onClick={() => navigate(`/chat/${m.id}`)}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-elevated)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
          >
            <div
              style={styles.avatarWrap}
              onClick={(e) => {
                e.stopPropagation();
                setProfileUserId(m.other_user.id);
              }}
            >
              <div style={styles.avatar}>
                {m.other_user.photo ? (
                  <img
                    src={`/uploads/${m.other_user.photo}`}
                    alt=""
                    style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                  />
                ) : (
                  m.other_user.display_name?.[0]?.toUpperCase() || '?'
                )}
              </div>
              {isOnline && <span className="online-dot" />}
            </div>

            <div style={styles.chatInfo}>
              <div style={styles.chatName}>{m.other_user.display_name}</div>
              <div style={styles.chatPreview}>
                {m.last_message?.content || 'Say hello!'}
              </div>
            </div>

            <div style={styles.chatMeta}>
              <span style={styles.chatTime}>
                {formatTime(m.last_message?.created_at || m.created_at)}
              </span>
              {m.unread_count > 0 && (
                <span style={styles.unreadBadge}>{m.unread_count}</span>
              )}
            </div>
          </div>
        );
      })}

      {/* Profile modal */}
      {profileUserId && (
        <WallProfileModal
          userId={profileUserId}
          onClose={() => setProfileUserId(null)}
        />
      )}
    </div>
  );
}
